import {
  Journey,
  JourneyElementCommon,
  Traveler,
} from '@dapi/sdk';
import {
  combineBoardingPassResults,
  createBoardingPassData,
  createBoardingPassResult,
  todayPlusNdays,
} from '@dapi/sdk/spec';
import {
  ffSVIdentificationAlshehri,
  testSv,
} from '../../../e2e/config/index';
import {
  frequentFlyerCardWithTierDetails,
} from './mock-frequent-flyer-card';
import {
  acceptedRoundTripJourneys,
  journeyElementIds,
  multiLegJourneyElementIds,
  multiLegJourneys,
  multiLegMultiPaxJourneyElementIds,
  multiLegMultiPaxJourneys,
} from './mock-journeys';

const dummyEticketNumber = '172240031798601';

export const defaultBoardingPassResult = combineBoardingPassResults(undefined, {
  journeyElementIds
}).items;

export const sentBoardingPassResult = combineBoardingPassResults([createBoardingPassResult({
  template: {
    ...defaultBoardingPassResult[0],
    result: 'sent'
  }
})
]).items;

export const sentBoardingPassResultWithDropOffTime = combineBoardingPassResults([createBoardingPassResult({
  template: {
    ...defaultBoardingPassResult[0],
    result: 'returnedBoardingPassData',
    boardingPassData: createBoardingPassData({
      template: {
        baggageInformation: {
          baggageDropClosingDateTime: todayPlusNdays('8:20', 2)
        },
        legs: [{
          boardingGate: '3',
          boardingDateTime: todayPlusNdays('10:00', 2)
        }]
      }
    })
  }
})
]).items;

export const returnedPDFBoardingPassResult = combineBoardingPassResults([createBoardingPassResult({
  template: {
    ...defaultBoardingPassResult[0],
    result: 'returnedDocumentURL'
  }
})
]).items;

export const returnedBoardingPassResult = combineBoardingPassResults([createBoardingPassResult({
  template: {
    ...defaultBoardingPassResult[0],
    result: 'returnedBoardingPassData'
  }
})
]).items;

export const inhibitedBoardingPassResult = combineBoardingPassResults([createBoardingPassResult({
  template: {
    ...defaultBoardingPassResult[0],
    result: 'inhibited'
  }
})
]).items;

export const etktAndFFBoardingPassResult = combineBoardingPassResults([{
  boardingPassData: {
    frequentFlyerCards: [
      {
        companyCode: testSv.airlineCode,
        cardNumber: ffSVIdentificationAlshehri.frequentFlyerNumber
      }
    ],
    ticketNumber: dummyEticketNumber
  }
}], {
  journeyElementIds
}).items;

export const etktAndFFWithTierDetailsBoardingPassResult = combineBoardingPassResults([{
  boardingPassData: {
    frequentFlyerCards: [
      {
        companyCode: testSv.airlineCode,
        cardNumber: frequentFlyerCardWithTierDetails.cardNumber
      }
    ],
    ticketNumber: dummyEticketNumber
  }
}], {
  journeyElementIds
}).items;

export const boardingPassResultWithChangeSeatNumber = combineBoardingPassResults([{
  boardingPassData: {
    legs: [
      {
        seat: {
          cabin: 'Y',
          isInfantOnSeat: false,
          seatCharacteristicsCodes: ['W', 'N', 'CH'],
          seatNumber: '2B'
        }
      }

    ]

  }
}], {
  journeyElementIds
}).items;

export const boardingPassResultWithFreeSeatNumber = combineBoardingPassResults([{
  boardingPassData: {
    legs: [
      {
        seat: {
          cabin: 'Y',
          isInfantOnSeat: false,
          seatCharacteristicsCodes: ['W', 'N', 'CH'],
          seatNumber: '2A'
        }
      }

    ]

  }
}], {
  journeyElementIds
}).items;

/**
 * Returns a mock barcode stream
 *
 * @param traveler the given traveler
 * @param originLocationCode the given origin location code
 * @param destinationLocationCode the given destination location code
 * @returns string
 */
export function getMockBarCodeStream(traveler: Traveler, originLocationCode = 'NCE', destinationLocationCode = 'LHR'): string {
  const formattedName = `M1${traveler.names?.[0].lastName || 'Doe'}/${traveler.names?.[0].firstName || 'John'}`.toUpperCase();
  return `${formattedName}            EJHODGV ${originLocationCode}${destinationLocationCode}6X 9511 248Y024A0001 347>5180 M    BKE              2922824098774240 6X `;
}

/**
 * Creates a mock boarding pass result for a round trip multi-pax journey
 *
 * @param roundTripJourney the given round trip journey
 * @param seatsForEachLeg the list of seats for each leg
 * @param isChargeable whether the seat is chargeable
 * @returns boarding pass result
 */
export function createRoundTripBoardingPassResult(
  roundTripJourney: Journey,
  seatsForEachLeg: string[] = ['1A'], // Default to '1A' if no seats provided
  isChargeable = false // Default to false
) {
  const {journeyElements} = roundTripJourney;
  const mockBoardingPassResult = journeyElements.map((journeyElement: JourneyElementCommon, index: number) => {
    const traveler = journeyElement.journeyElement.traveler;
    const originLocationCode = journeyElement.journeyElement.flight.departure.locationCode;
    const destinationLocationCode = journeyElement.journeyElement.flight.arrival.locationCode;
    const seatNumber = traveler.passengerTypeCode === 'INF' ? 'INF' // For infants, assign 'INF'
      : (seatsForEachLeg && seatsForEachLeg[index] ? seatsForEachLeg[index] // Use passed seat or default
        : traveler.passengerTypeCode === 'ADT' ? '1A' : '1B'); // For adults ('ADT'), use '1A'; for others (e.g., children), use '1B'

    return createBoardingPassResult({
      template: {
        boardingPassData: createBoardingPassData({
          template: {
            baggageInformation: {
              baggageDropClosingDateTime: todayPlusNdays('8:20', 2)
            },
            barcodeStream: getMockBarCodeStream(traveler, originLocationCode, destinationLocationCode),
            legs: [{
              arrival: {
                dateTime: originLocationCode === 'NCE' ? todayPlusNdays('10:00', 2) : todayPlusNdays('18:00', 2),
                locationCode: destinationLocationCode,
                terminal: '2'
              },
              barcodeStream: getMockBarCodeStream(traveler, originLocationCode, destinationLocationCode),
              boardingDateTime: originLocationCode === 'NCE' ? todayPlusNdays('08:20', 2) : todayPlusNdays('16:30', 2),
              boardingGate: '3',
              departure: {
                dateTime: originLocationCode === 'NCE' ? todayPlusNdays('09:00', 2) : todayPlusNdays('17:00', 2),
                locationCode: originLocationCode,
                terminal: '5'
              },
              seat: {
                cabin: 'Y',
                seatNumber,
                isInfantOnSeat: traveler.passengerTypeCode === 'INF',
                seatCharacteristicsCodes: ['W', 'N', ...(isChargeable ? ['CH'] : [])]
              }
            }],
            traveler
          }
        }),
        documentType: 'boardingPass',
        journeyElementIds: multiLegJourneyElementIds,
        result: 'returnedBoardingPassData'
      }
    });
  });
  return combineBoardingPassResults(mockBoardingPassResult, {
    journeyElementIds: roundTripJourney.journeyElements.map((je: JourneyElementCommon) => je.id)
  }).items;
}

/**
 * Creates a mock boarding pass result for a multi leg journey
 *
 * @param seatsForEachLeg the list of seats for each leg
 * @param isChargeable boolean to indicate if the boarding pass is chargeable
 */
export function createMultiLegBoardingPassResult(seatsForEachLeg: string[] = ['1A'], isChargeable = false) {
  const differentSeatsForEachLeg = seatsForEachLeg.length > 1;
  const traveler = multiLegJourneys[0].journeyElements[0].journeyElement.traveler;
  return combineBoardingPassResults([
    createBoardingPassResult({
      template: {
        boardingPassData: createBoardingPassData({
          template: {
            baggageInformation: {
              baggageDropClosingDateTime: todayPlusNdays('8:20', 2)
            },
            barcodeStream: getMockBarCodeStream(traveler),
            legs: seatsForEachLeg.map((seat, index) => ({
              arrival: {
                dateTime: differentSeatsForEachLeg ? (index === 0 ? todayPlusNdays('9:20', 2) : todayPlusNdays('10:00', 2)) : todayPlusNdays('10:00', 2),
                locationCode: differentSeatsForEachLeg ? index === 0 ? 'CDG' : 'LHR' : 'LHR',
                terminal: '2'
              },
              barcodeStream: getMockBarCodeStream(traveler, differentSeatsForEachLeg ? (index > 0 ? 'CDGLHR' : 'NCECDG') : 'NCELHR'),
              boardingDateTime: differentSeatsForEachLeg ? index === 0 ? todayPlusNdays('8:40', 2) : todayPlusNdays('9:25', 2) : todayPlusNdays('8:40', 2),
              boardingGate: '3',
              departure: {
                dateTime: differentSeatsForEachLeg ? index === 0 ? todayPlusNdays('9:00', 2) : todayPlusNdays('9:30', 2) : todayPlusNdays('9:00', 2),
                locationCode: differentSeatsForEachLeg ? index === 0 ? 'NCE' : 'CDG' : 'NCE',
                terminal: differentSeatsForEachLeg ? index === 0 ? '5' : '2' : '5'
              },
              seat: {
                cabin: 'Y',
                isInfantOnSeat: false,
                seatCharacteristicsCodes: [
                  'W',
                  'N',
                  ...(isChargeable ? ['CH'] : [])
                ],
                seatNumber: seat
              }
            })),
            traveler
          }
        }),
        documentType: 'boardingPass',
        journeyElementIds: multiLegJourneyElementIds,
        result: 'returnedBoardingPassData'
      }
    })
  ],
  {
    journeyElementIds: multiLegJourneyElementIds
  }).items;
}

/**
 * Creates a mock boarding pass result for a multi leg journey
 *
 * @param seatsForEachLeg the list of seats for each leg
 * @param isChargeable boolean to indicate if the boarding pass is chargeable
 */
export function createMultiLegMultiPAXBoardingPassResult(seatsForEachLeg: string[] = ['1A'], isChargeable = false) {
  const differentSeatsForEachLeg = seatsForEachLeg.length > 1;
  const traveler = multiLegMultiPaxJourneys[0].journeyElements[0].journeyElement.traveler;
  return combineBoardingPassResults([
    createBoardingPassResult({
      template: {
        boardingPassData: createBoardingPassData({
          template: {
            baggageInformation: {
              baggageDropClosingDateTime: todayPlusNdays('8:20', 2)
            },
            barcodeStream: getMockBarCodeStream(traveler),
            legs: seatsForEachLeg.map((seat, index) => ({
              arrival: {
                dateTime: differentSeatsForEachLeg ? (index === 0 ? todayPlusNdays('9:20', 2) : todayPlusNdays('10:00', 2)) : todayPlusNdays('10:00', 2),
                locationCode: differentSeatsForEachLeg ? index === 0 ? 'CDG' : 'LHR' : 'LHR',
                terminal: '2'
              },
              barcodeStream: getMockBarCodeStream(traveler, differentSeatsForEachLeg ? (index > 0 ? 'CDGLHR' : 'NCECDG') : 'NCELHR'),
              boardingDateTime: differentSeatsForEachLeg ? index === 0 ? todayPlusNdays('8:40', 2) : todayPlusNdays('9:25', 2) : todayPlusNdays('8:40', 2),
              boardingGate: '3',
              departure: {
                dateTime: differentSeatsForEachLeg ? index === 0 ? todayPlusNdays('9:00', 2) : todayPlusNdays('9:30', 2) : todayPlusNdays('9:00', 2),
                locationCode: differentSeatsForEachLeg ? index === 0 ? 'NCE' : 'CDG' : 'NCE',
                terminal: differentSeatsForEachLeg ? index === 0 ? '5' : '2' : '5'
              },
              seat: {
                cabin: 'Y',
                isInfantOnSeat: false,
                seatCharacteristicsCodes: [
                  'W',
                  'N',
                  ...(isChargeable ? ['CH'] : [])
                ],
                seatNumber: seat
              }
            })),
            traveler
          }
        }),
        documentType: 'boardingPass',
        journeyElementIds: multiLegMultiPaxJourneyElementIds,
        result: 'returnedBoardingPassData'
      }
    })
  ],
  {
    journeyElementIds: multiLegMultiPaxJourneyElementIds
  }).items;
}

export const returnedBlindBoardingPassResult = combineBoardingPassResults([createBoardingPassResult({
  template: {
    ...defaultBoardingPassResult[0],
    boardingPassData: {
      legs: [
        {
          arrival: {
            dateTime: todayPlusNdays('12:50', 1),
            locationCode: 'LHR',
            terminal: ''
          },
          boardingDateTime: todayPlusNdays('09:30', 1),
          boardingGate: '',
          departure: {
            dateTime: todayPlusNdays('10:00', 1),
            estimatedDateTime: todayPlusNdays('', 0),
            locationCode: 'NCE',
            terminal: ''
          },
          seat: {
            blockId: '',
            cabin: 'M',
            isInfantOnSeat: false,
            seatCharacteristicsCodes: [
              'N'
            ],
            seatNumber: '8E',
            seatType: ''
          },
          services: [
            {
              code: 'BLND',
              comment: 'HK1'
            }
          ]
        }
      ]
    },
    result: 'returnedBoardingPassData',
    documentType: 'confirmation'
  }
})
]).items;

/** Mock boarding pass result for different seats for a multi leg journey */
export const multiLegBoardingPassResultForDifferentSeats = createMultiLegBoardingPassResult(['2C', '2E']);

/** Mock boarding pass result for the same free seats for a multi leg journey */
export const multiLegBoardingPassResultForSameFreeSeats = createMultiLegBoardingPassResult(['2A']);

/** Mock boarding pass result for free seats for a multi leg multi pax journey */
export const multiLegMultiPaxBoardingPassResultForSameFreeSeats = createMultiLegMultiPAXBoardingPassResult(['2A', '2B']);

/** Mock boarding pass result for the same chargeable seats for a multi leg journey */
export const multiLegBoardingPassResultForSameChargeableSeats = createMultiLegBoardingPassResult(['1A'], true);

/** Mock boarding pass result for the same non exit-row chargeable seats for a multi leg journey */
export const multiLegBoardingPassResultForSameNonExitRowChargeableSeats = createMultiLegBoardingPassResult(['1B'], true);

/** Mock boarding pass result for a multi-pax round trip journey */
export const roundTripBoardingPassResult = createRoundTripBoardingPassResult(acceptedRoundTripJourneys[2]);

/** Mock boarding pass result for a different seat multi-pax oneway trip journey */
export const roundTripBoardingPassWithSeats = createRoundTripBoardingPassResult(acceptedRoundTripJourneys[2], ['2B', '2A']);

/** Mock boarding pass result for a different chargable seat multi-pax oneway trip journey */
export const roundTripBoardingPassWithSeats2 = createRoundTripBoardingPassResult(acceptedRoundTripJourneys[2], ['1A', '10A'], true);
